import type { StatusSegment } from './DeliveryStatusBar';
import { VisitStatus } from '../enums';
import { useState } from 'react';
import { formatPercentageLabel } from '../utils';

interface StatusSegmentBarProps {
  segments: StatusSegment[];
  total: number;
  selectedStatus?: VisitStatus | null;
  onToggle: (status: VisitStatus) => void;
}

const StatusSegmentBar = ({
  segments,
  total,
  selectedStatus,
  onToggle,
}: StatusSegmentBarProps) => {
  const [hoveredStatus, setHoveredStatus] = useState<VisitStatus | null>(null);
  const visibleSegments = segments.filter((segment) => segment.value > 0);

  return (
    <div className='flex h-6 w-full gap-0.5 rounded-full bg-gray-100'>
      {visibleSegments.map((segment, index) => {
        const isSelected = selectedStatus === segment.status;
        const isDimmed = Boolean(selectedStatus) && !isSelected;
        const isHovered = hoveredStatus === segment.status;
        const label = segment.value === 1 ? 'visita' : 'visitas';
        const percentage = (segment.value / total) * 100;
        const showInlineLabel = percentage >= 8;
        const accessibleLabel = `${segment.name}: ${segment.value} ${label} (${Math.round(percentage)}%)`;

        return (
          <button
            key={segment.status}
            type='button'
            onClick={() => onToggle(segment.status)}
            onMouseEnter={() => setHoveredStatus(segment.status)}
            onMouseLeave={() => setHoveredStatus(null)}
            onFocus={() => setHoveredStatus(segment.status)}
            onBlur={() => setHoveredStatus(null)}
            aria-label={accessibleLabel}
            className={`relative flex h-full cursor-pointer items-center justify-center transition-opacity ${
              index === 0 ? 'rounded-l-full' : ''
            } ${index === visibleSegments.length - 1 ? 'rounded-r-full' : ''}`}
            style={{
              backgroundColor: segment.color,
              flexBasis: `${percentage}%`,
              opacity: isDimmed ? 0.3 : 1,
            }}
          >
            {showInlineLabel && (
              <span className='text-xs font-semibold text-white'>
                {Math.round(percentage)}%
              </span>
            )}

            {isHovered && (
              <div
                role='tooltip'
                className='pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-left shadow-lg'
              >
                <div className='flex items-center gap-1.5 text-xs font-semibold text-gray-800'>
                  <span
                    className='h-2 w-2 shrink-0 rounded-full'
                    style={{ backgroundColor: segment.color }}
                  />
                  {segment.name}
                </div>
                <div className='mt-0.5 text-xs text-gray-500'>
                  <span className='font-bold text-gray-900'>
                    {segment.value} {label}
                  </span>{' '}
                  · {formatPercentageLabel(percentage)}%
                </div>
                <div className='absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-white' />
                <div className='absolute left-1/2 top-full -z-10 -translate-x-1/2 translate-y-[1px] border-4 border-transparent border-t-gray-200' />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default StatusSegmentBar;
